import React, { useState, useEffect, useRef, useCallback } from "react";
import { quickCapture, captureToCategory, polishNoteAfterSave } from "../../services/workspaceService";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useEditLock } from "../../context/EditLockContext";
import {
  isPasteDump,
  smartPaste,
} from "../../utils/normalizePaste";
import { getCodeProject, loadCodeProjectId, resolveCodeProjects } from "../../config/codeProjects";
import deskGlass, { ACCENT } from "../../styles/deskGlass";
import { PenLine, Plus, Loader2, X } from "lucide-react";

const styles = deskGlass;

export default function QuickCapture() {
  const { settings } = useWorkspace();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const textareaRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "N") {
      e.preventDefault();
      if (!locked) setIsOpen(true);
    }
    if (e.key === "Escape") {
      setIsOpen(false);
      setText("");
    }
  }, [locked]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const open = () => {
      if (!locked) setIsOpen(true);
    };
    window.addEventListener("open-quick-capture", open);
    return () => window.removeEventListener("open-quick-capture", open);
  }, [locked]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const raw = text.trim();
    if (!raw || saving || locked) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (isPasteDump(raw)) {
        const project = getCodeProject(loadCodeProjectId(), settings);
        const receipt = await captureToCategory("código", smartPaste(raw), {
          settings,
          notebookName: project?.notebookName,
          color: project?.color,
        });
        if (receipt?.needsPolish) {
          polishNoteAfterSave({
            deskId: receipt.deskId,
            drawerId: receipt.drawerId,
            notebookId: receipt.notebookId,
            textId: receipt.textId,
            content: receipt.content,
            title: receipt.title,
          }).catch(() => {});
        }
      } else {
        await quickCapture(smartPaste(raw));
      }
      setText("");
      setIsOpen(false);
    } catch (err) {
      console.error("[QuickCapture] save failed:", err);
      setSaveError(err.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDownTextarea = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData?.getData("text/plain");
    if (!pasted) return;
    e.preventDefault();
    const clean = smartPaste(pasted);
    const el = e.target;
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const merged = text.slice(0, start) + clean + text.slice(end);
    setText(smartPaste(merged));
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={() => { setIsOpen(false); setText(""); }}>
      <div style={styles.modalPanel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.glassHeader}>
          <PenLine size={18} color="#fff" />
          <h2 style={styles.glassTitle}>Captura Rápida</h2>
          <button
            type="button"
            onClick={() => { setIsOpen(false); setText(""); }}
            aria-label="Cerrar"
            style={{ ...styles.viewToggle, marginLeft: "auto", border: "none", background: "transparent", padding: "4px" }}
          >
            <X size={18} />
          </button>
        </div>
        <textarea
          ref={textareaRef}
          value={text}
          disabled={locked}
          onChange={(e) => setText(e.target.value)}
          onPaste={handlePaste}
          onKeyDown={handleKeyDownTextarea}
          placeholder="¿Qué estás pensando? Pegá LLM, CLI o código (va a Código)"
          rows={4}
          style={{ ...styles.glassTextarea, width: "100%", boxSizing: "border-box" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", flexWrap: "wrap", gap: "8px" }}>
          <span style={{ ...styles.subtext, ...(saveError ? { color: "#ffb4b4" } : {}) }}>
            {saveError || "Enter para guardar · Esc para cerrar"}
          </span>
          <button
            onClick={handleSubmit}
            disabled={locked || saving || !text.trim()}
            style={{
              ...styles.glassActionBtn,
              ...styles.viewToggleActive,
              padding: "8px 18px",
              opacity: locked || saving || !text.trim() ? 0.6 : 1,
              cursor: locked || saving || !text.trim() ? "not-allowed" : "pointer",
            }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} color={ACCENT} />}
            {saving ? " Guardando…" : " Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
